import React from 'react';
import { ArrowRight, BookOpen, Code2, Cpu, Database, FileImage, GitBranch, Rss, Search } from 'lucide-react';
import { CodeBlock } from './CodeBlock';

const stats = [
  ['725K+', 'papers with abstracts'],
  ['613K+', 'paper ↔ code links'],
  ['15.5K', 'datasets'],
  ['9.5K', 'methods'],
  ['59K+', 'eval results'],
];

const features = [
  {
    icon: Search,
    name: 'Semantic search on gobed',
    detail:
      'Queries hit IVF-HNSW-PQ indexes over 725K paper embeddings on the gobed int8 engine: millisecond lookups on GPU CAGRA. The same engine is sold as the gobed addon.',
  },
  {
    icon: GitBranch,
    name: 'Code-linked results',
    detail:
      'Every paper joins 613K paper-to-repository links plus methods, datasets, and leaderboard evals, so a search lands on runnable code.',
  },
  {
    icon: Rss,
    name: 'Live arXiv crawler',
    detail:
      'A Go crawler ingests arXiv submissions continuously and backfills history, so coverage is gap-free since the archive dumps ended.',
  },
  {
    icon: FileImage,
    name: 'Figure-aware social cards',
    detail:
      'Every paper gets an OG card from its best PDF figure: MuPDF renders pages in-process, embedded images are extracted and scored, and the card is drawn in Go. No Python, no subprocesses.',
  },
  {
    icon: BookOpen,
    name: 'Embedded PDF reader',
    detail: 'Open any paper in a viewer with the PDF inline, plus linked code repos, tasks, and related papers.',
  },
  {
    icon: Cpu,
    name: 'One Go binary',
    detail:
      'fasthttp + SQLite WAL + gobed in one process. The whole 725K-paper engine, its crawler, and its image pipeline deploy as two binaries and a systemd unit.',
  },
];

const apiRows = [
  ['GET /api/papers/search?q=…&type=papers|methods|datasets', 'authenticated semantic search for apps, CLI, and MCP'],
  ['GET /api/papers/search?q=…&type=github_code', 'search indexed GitHub source files'],
  ['GET /api/papers/search?q=…&type=repos', 'search tracked code repositories'],
  ['GET /api/papers/item?id=…', 'full paper record as JSON or Markdown'],
  ['GET /papers/:id', 'canonical app.nz paper detail page'],
];

const apiExample = `curl "https://app.nz/api/papers/search?q=state+space+models&type=papers&limit=5" \\
  -H "Authorization: Bearer $APP_API_KEY"`;

export const PapersShowcasePage: React.FC = () => {
  return (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(167,139,250,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-5xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/25 bg-violet-400/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-violet-300">
          <BookOpen size={12} /> app.nz/papers · research search
        </div>
        <h1 className="mt-6 max-w-[20ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          Papers: semantic search over 725K ML papers and their code
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-white/72">
          A papers-with-code search engine rebuilt in Go: gobed vector search over every abstract, method,
          and dataset, a live arXiv crawler, an embedded PDF reader, and social cards from each paper's
          figures. Live at app.nz/papers.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/papers" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-violet-400 px-6 text-sm font-bold text-black transition hover:bg-violet-300">
            Search papers free <ArrowRight size={16} />
          </a>
        </div>
        <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-5">
          {stats.map(([value, label]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
              <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">{label}</dt>
              <dd className="mt-1 font-display text-xl font-bold text-violet-300">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-violet-400/90">the product</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Search, read, and trace papers to code</h2>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
            <img src="/assets/papers-home.webp" alt="app.nz Papers semantic search results" loading="lazy" className="w-full" />
            <figcaption className="px-5 py-4 text-sm leading-6 text-white/60">
              Semantic results across papers, methods, and datasets, ranked by gobed embedding similarity and joined with code links.
            </figcaption>
          </figure>
          <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
            <img src="/assets/papers-viewer.webp" alt="app.nz Papers reader with embedded PDF" loading="lazy" className="w-full" />
            <figcaption className="px-5 py-4 text-sm leading-6 text-white/60">
              The reader view: abstract, authors, tasks, linked repositories, and the embedded PDF.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-violet-400/90">figure-aware cards</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Every paper ships with a generated social card</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Share a paper link and the preview comes from the paper itself: in-process MuPDF renders the PDF,
          scores embedded figures against detected page crops, falls back to an abstract screenshot, then
          composes a 1200×630 card with the winning figure's palette, typography, and layout. The pipeline is
          Go inside the server; the Python/Pillow version was retired.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {['/assets/papers-og-1.webp', '/assets/papers-og-2.webp'].map((src) => (
            <img key={src} src={src} alt="Generated paper social card" loading="lazy" className="w-full rounded-2xl border border-white/10" />
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-violet-400/90">under the hood</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">How the engine is built</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.name} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <f.icon size={20} className="text-violet-400" />
              <h3 className="mt-4 font-display text-lg font-bold">{f.name}</h3>
              <p className="mt-2 text-sm leading-7 text-white/65">{f.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-violet-400/90">api</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Everything is an endpoint</h2>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.04]">
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">Endpoint</th>
                <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {apiRows.map((r) => (
                <tr key={r[0]} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 font-mono text-[12px] font-semibold text-white">{r[0]}</td>
                  <td className="px-4 py-3 leading-6 text-white/68">{r[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6">
          <CodeBlock code={apiExample} language="bash" preClassName="!text-[13px] !leading-6" />
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(167,139,250,0.14),transparent_60%)]" />
        <div className="relative">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">From query to running code</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            Papers search is free, wired into app.nz agents and Deep Research, and powered by the same
            gobed engine you can attach to your own apps.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="/papers" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-violet-400 px-6 text-sm font-bold text-black transition hover:bg-violet-300">
              Open app.nz Papers <ArrowRight size={16} />
            </a>
            <a href="/addons/gobed" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              The engine behind it <Database size={16} />
            </a>
            <a href="/deep-research" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              Deep Research <Code2 size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  </main>
  );
};

export default PapersShowcasePage;
